import React, { useState, useEffect } from 'react';
import { useAuth } from '../../components/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaEnvelope, FaShieldAlt, FaTrashAlt, FaCheckCircle, FaUserEdit } from 'react-icons/fa';

const Profile = () => {
  const { user, verifyPassword, updateUserProfile, updateUserPassword, deleteAccount, loading } = useAuth();
  const navigate = useNavigate();

  const [isVerified, setIsVerified] = useState(false);
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
    if (user) {
      setName(user.name || user.displayName || '');
      setEmail(user.email || '');
    }
  }, [user, loading, navigate]);

  const handlePasswordVerify = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    setError('');
    
    const result = await verifyPassword(password);
    if (result.success) {
      setIsVerified(true);
      setError('');
    } else {
      setError(result.error);
    }
    setIsVerifying(false);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    setError('');
    setSuccess('');

    const result = await updateUserProfile({ name });
    if (result.success) {
      setSuccess('프로필이 성공적으로 업데이트되었습니다.');
    } else {
      setError(result.error);
    }
    setIsUpdating(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      setError('새 비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    setIsChangingPassword(true);
    const result = await updateUserPassword(newPassword);
    if (result.success) {
      setSuccess('비밀번호가 성공적으로 변경되었습니다.');
      setNewPassword('');
      setConfirmNewPassword('');
    } else {
      setError(result.error);
    }
    setIsChangingPassword(false);
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('정말로 회원을 탈퇴하시겠습니까? 모든 정보가 영구적으로 삭제됩니다.')) {
      const result = await deleteAccount();
      if (result.success) {
        alert('탈퇴가 완료되었습니다.');
        navigate('/');
      } else {
        setError(result.error);
      }
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  if (!isVerified) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col pt-20 sm:pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
              <FaLock className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              보안 인증
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              정보를 수정하기 위해 비밀번호를 다시 입력해주세요.
            </p>
          </div>

          <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-gray-100">
            <form className="space-y-6" onSubmit={handlePasswordVerify}>
              <div>
                <label className="block text-sm font-medium text-gray-700">이메일</label>
                <div className="mt-1 flex items-center p-3 bg-gray-50 rounded-lg text-gray-500 text-sm">
                  <FaEnvelope className="mr-2" /> {email}
                </div>
              </div>

              <div>
                <label htmlFor="verify-password" className="block text-sm font-medium text-gray-700">비밀번호</label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                    <FaLock className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    id="verify-password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="비밀번호를 입력하세요"
                  />
                </div>
              </div>

              {error && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</div>}

              <button
                type="submit"
                disabled={isVerifying}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none transition-colors"
              >
                {isVerifying ? '인증 중...' : '인증하기'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-20 sm:pt-32 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="text-center text-3xl font-extrabold text-gray-900">내 정보 관리</h2>
        <p className="mt-2 text-center text-sm text-gray-600">회원 정보를 확인하고 수정할 수 있습니다.</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-gray-100">
          <form className="space-y-6" onSubmit={handleUpdateProfile}>
            <div>
              <label className="block text-sm font-medium text-gray-700">이메일 계정</label>
              <div className="mt-1 flex items-center p-3 bg-gray-50 rounded-lg text-gray-500">
                <FaEnvelope className="mr-3 text-gray-400" /> 
                <span className="font-medium text-gray-600">{email}</span>
                <span className="ml-auto text-xs bg-gray-200 px-2 py-0.5 rounded text-gray-500">변경 불가</span>
              </div>
            </div>

            <div>
              <label htmlFor="profile-name" className="block text-sm font-medium text-gray-700">이름</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <FaUser className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="profile-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="이름을 입력하세요"
                />
              </div>
            </div>

            {error && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</div>}
            {success && (
              <div className="text-sm text-green-600 bg-green-50 p-3 rounded-lg flex items-center">
                <FaCheckCircle className="mr-2" /> {success}
              </div>
            )}

            <button
              type="submit"
              disabled={isUpdating}
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none transition-colors"
            >
              {isUpdating ? '저장 중...' : (
                <><FaUserEdit className="mr-2" /> 정보 수정하기</>
              )}
            </button>
          </form>

          <div className="mt-10 border-t border-gray-100 pt-6">
            <h4 className="text-sm font-bold text-gray-900 flex items-center mb-4">
              <FaLock className="mr-2 text-blue-500" /> 비밀번호 변경
            </h4>
            <form className="space-y-4" onSubmit={handlePasswordChange}>
              <div>
                <label htmlFor="new-password" Name="block text-sm font-medium text-gray-700">새 비밀번호</label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="새 비밀번호 (6자 이상)"
                />
              </div>
              <div>
                <label htmlFor="confirm-new-password" Name="block text-sm font-medium text-gray-700">새 비밀번호 확인</label>
                <input
                  id="confirm-new-password"
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="비밀번호 재입력"
                />
              </div>
              <button
                type="submit"
                disabled={isChangingPassword}
                className="w-full flex justify-center py-2 px-4 border border-blue-600 text-blue-600 rounded-lg text-sm font-bold hover:bg-blue-600 hover:text-white transition-all disabled:opacity-50"
              >
                {isChangingPassword ? '변경 중...' : '비밀번호 변경하기'}
              </button>
            </form>
          </div>

          <div className="mt-10 border-t border-gray-100 pt-6">
            <h4 className="text-sm font-bold text-red-600 flex items-center mb-4">
              <FaShieldAlt className="mr-2" /> 위험 구역
            </h4>
            <div className="bg-red-50 p-4 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-red-800">회원 탈퇴</p>
                <p className="text-xs text-red-600 mt-1">계정 및 모든 기록이 영구적으로 삭제됩니다.</p>
              </div>
              <button
                onClick={handleDeleteAccount}
                className="flex items-center px-4 py-2 border border-red-200 bg-white text-red-600 rounded-lg text-xs font-bold hover:bg-red-600 hover:text-white transition-all shadow-sm"
              >
                <FaTrashAlt className="mr-1" /> 탈퇴하기
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
